import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './TaskPage.css';
import TaskComponent from '../components/TaskComponent';
import AudioComponent, { GameState } from '../components/AudioComponent';
import ChatComponent from '../components/ChatComponent';
import { Scenario } from '../services/scenarios';
import { useScenarioSession } from '../hooks/useScenarioSession';
import { useScenarioSessionStore } from '../stores/scenarioSessionStore';

const TaskPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const scenario = (location.state as { scenario?: Scenario })?.scenario;
  
  const {
    messages,
    isInitialized,
    isListening,
    isProcessing,
    isSpeaking,
    isConversationFinished,
    isLoadingTranscript,
    canContinueConversation,
    error,
    chatRetryState,
    setScenario,
    setError,
    startListening,
    stopListening,
    clearChat,
    speakOpeningMessage,
    processUserMessage,
    retryLastMessage,
    askNewQuestion,
    replayLastMessage,
  } = useScenarioSession();

  const { resetSession } = useScenarioSessionStore();

  const [gameState, setGameState] = useState<GameState>('initial');
  const [isChatVisible, setIsChatVisible] = useState(false);

  // Set scenario when TaskPage loads
  useEffect(() => {
    if (scenario) {
      setScenario(scenario);
    }
  }, [scenario, setScenario]);

  // Redirect to homepage if no active session and no scenario provided
  useEffect(() => {
    if (isInitialized && !isLoadingTranscript && !scenario && messages.length === 0) {
      // No scenario passed from navigation and no existing conversation
      navigate('/');
    }
  }, [isInitialized, isLoadingTranscript, scenario, messages.length, navigate]);

  // Detect if there's already a conversation in progress and set appropriate initial state
  useEffect(() => {
    if (isInitialized && !isLoadingTranscript) {
      if (isConversationFinished) {
        setGameState('conversation-finished');
      } else if (canContinueConversation) {
        // Conversation has started, user can continue speaking
        setGameState('mic-ready');
      } else if (messages.length === 0) {
        // Fresh session, stay in initial state
        setGameState('initial');
      }
    }
  }, [isInitialized, isLoadingTranscript, canContinueConversation, isConversationFinished, messages.length]);

  // Update game state based on hook state
  useEffect(() => {
    if (isConversationFinished) {
      setGameState('conversation-finished'); // Nieuwe state voor afgerond gesprek
    } else if (isListening) {
      setGameState('mic-listening'); // Blue microphone - "Luisteren"
    } else if (isProcessing) {
      setGameState('robot-thinking'); // Yellow robot - "Aan het nadenken"
    } else if (isSpeaking) {
      setGameState('robot-speaking'); // Green robot - "Aan het praten"
    } else if (isInitialized && gameState !== 'initial') {
      setGameState('mic-ready'); // Yellow microphone - "Klik om te spreken"
    }
  }, [isListening, isProcessing, isSpeaking, isInitialized, isConversationFinished, gameState]);

  const startGame = async (): Promise<void> => {
    if (!isInitialized) return;
    
    try {
      setError(null);
      
      // Speak the opening message from the scenario
      await speakOpeningMessage();
      
      // After speaking, set to ready state
      setGameState('mic-ready');
    } catch (err) {
      console.error('Error starting game:', err);
      setError('Fout bij starten van het gesprek: ' + (err as Error).message);
      setGameState('initial');
    }
  };

  const handleStartListening = async (): Promise<void> => {
    if (!isInitialized || gameState !== 'mic-ready' || isConversationFinished) return;
    
    try {
      await startListening();
    } catch (err) {
      console.error('Speech recognition error:', err);
      setGameState('mic-ready');
    }
  };

  const handleStopListening = async (): Promise<void> => {
    if (gameState === 'mic-listening') {
      try {
        await stopListening();
      } catch (err) {
        console.error('Error stopping listening:', err);
        setError('Fout bij stoppen luisteren: ' + (err as Error).message);
      }
    }
  };

  const handleClearChat = (): void => {
    clearChat();
    setGameState('initial');
  };

  const handleNewConversation = (): void => {
    clearChat();
    setGameState('initial');
  };

  const handleStopConversation = (): void => {
    // Clear the chat and reset session like when AI finishes
    clearChat();
    resetSession();
    // Navigate back to homepage
    navigate('/');
  };

  const toggleChatVisibility = (): void => {
    setIsChatVisible(prev => !prev);
  };
  
  const handleSendMessage = async (message: string): Promise<void> => {
    if (!isInitialized || isConversationFinished) return;
    
    try {
      await processUserMessage(message, null); // null voor audioBlob aangezien dit een getypte bericht is
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Fout bij versturen van bericht: ' + (err as Error).message);
    }
  };

  return (
    <div className="TaskPage">
      <header className="header">
        <h1>{scenario?.title || 'In gesprek met AI'}</h1>
      </header>
      
      <main className="main-content">
        <div className="left-panel">
          <TaskComponent htmlDescription={scenario?.htmlDescription} />
        </div>
        
        <div className="right-panel">
          <AudioComponent
            gameState={gameState}
            onStartGame={startGame}
            onStartListening={handleStartListening}
            onStopListening={handleStopListening}
            onNewConversation={handleNewConversation}
            disabled={!isInitialized}
            error={error}
            chatRetryState={chatRetryState}
            onRetryLastMessage={retryLastMessage}
            onAskNewQuestion={askNewQuestion}
            onClearError={() => setError(null)}
            onSetGameState={setGameState}
            onReplayLastMessage={replayLastMessage}
            hasAssistantMessages={messages.some(msg => msg.type === 'assistant')}
          />
          

        </div>
      </main>
      
      {/* Fixed button container - bottom right */}
      <div className="fixed-buttons-container">
        {(messages.length > 0 && !isConversationFinished) && (
          <button 
            className="stop-conversation-button-fixed"
            onClick={handleStopConversation}
            title="Stop gesprek"
          >
            Stop gesprek
          </button>
        )}
        <ChatComponent
          messages={messages}
          isVisible={isChatVisible}
          onToggleVisibility={toggleChatVisibility}
          onClearChat={handleClearChat}
          onSendMessage={isConversationFinished ? undefined : handleSendMessage}
          isProcessing={isProcessing}
        />
      </div>
    </div>
  );
};

export default TaskPage;